import React from 'react';
import HeroBannerV4 from '@ajat/design-system/ContentBlocks/HeroBannerV4';
import { ComponentPage, DemoCard } from '../../shared';
import { HERO_V4, FOMO_V4 } from './contentDemoData';

export const meta = {
  component: HeroBannerV4,
  title: 'Hero Banner V4',
  group: 'Content Blocks',
  importPath: '@ajat/design-system/ContentBlocks/HeroBannerV4',
  figmaNode: '90:26891',
  description:
    'Photo hero with a check-mark USP bar and a FOMO card (Crown Student homepage, Figma Crownstudent-MCP 90:26891 / mobile 96:30960): a 520px banner on the brand gradient (component token --hero-v4-bg, raw Figma hex per brand) with the students cut-out hanging over its right half, white headline (h1-editorial, "\\n" = an authored line break) and copy, a white arrow CTA (desktop only — the Figma mobile frame has none); the gray-025 USP bar under it (lucide Check + label, dividers on desktop, a 2×2 grid on mobile); the card (opaque gray-025, no backdrop blur) with its tilted top edge (the Figma CurvedShape, drawn as a path from the measured height), the cap photo floating over its left half, the guaranteed delivery date, a days : hours : minutes : seconds countdown (zeros once the deadline has passed) and a full-width hero CTA (the DS Button `hero` variant) — right-aligned to the layout content edge and hanging over the bar on desktop; on mobile it sits 12px from the sides and the bottom of the photo block, which fills the first screen under the header so the CTA is always visible.',
  props: [
    ['title', 'string', "''", 'Headline (typo-h1-editorial, --hero-v4-ink = gray-0); "\\n" = an authored line break'],
    ['copy', 'string', "''", 'Body copy under the headline (typo-body, --hero-v4-ink)'],
    ['ctaLabel', 'string', "''", 'CTA label (Button secondary white, label in the brand primary-alternative) — desktop only'],
    ['ctaIcon', 'node', '<ArrowRight />', 'CTA icon'],
    ['ctaVariant', 'string', "'white'", 'CTA Button variant'],
    ['ctaClassName', 'string', "''", 'Extra class on the CTA (placement hooks; a skin is a `ctaVariant`)'],
    ['ctaHref', 'string', '—', 'CTA link target — renders the CTA as a real link'],
    ['ctaOnClick', 'func', '—', 'CTA handler'],
    ['image', 'string', '—', 'Desktop photo — a cut-out with alpha hanging over the banner’s right half (Figma geometry)'],
    ['imageMobile', 'string', '—', 'Mobile photo — the cut-out framed for the 420px block, cover (falls back to image)'],
    ['imageAlt', 'string', "''", 'Alt text for the photo'],
    ['usps', 'array', '[]', '[{ label }] — each a check mark + label under the banner; [] hides the bar'],
    ['fomo', 'object', 'null', '{ image, imageAlt, deliveryLabel, deliveryDate, countdownLabel, deadline (ISO or YYYY-MM-DD = end of day), countdownUnits { days, hours, minutes, seconds }, ctaLabel, ctaHref, ctaOnClick, ctaVariant } — the FOMO card; null hides it'],
    ['reveal', 'bool', 'false', 'BlurReveal entrance: title word by word → copy → CTA → card → USP items, the photo fading in alongside'],
    ['className', 'string', "''", 'Additional CSS class (other attributes reach the <section>)'],
  ],
  usage: [
    {
      title: 'On the Crown Student landing',
      code: `import HeroBannerV4 from '@ajat/design-system/ContentBlocks/HeroBannerV4';

<HeroBannerV4
  title={'Ett minne\\nför livet!'}
  copy="Vi vill att du ska få den bästa studenten! …"
  ctaLabel="Designa nu!"
  ctaOnClick={() => navigate('/configurator')}
  image={heroPhoto}
  imageMobile={heroPhotoMobile}
  imageAlt="Fem studenter i vita studentmössor"
  usps={[{ label: 'Högsta kvalité' }, { label: 'Fri Frakt' }, { label: 'Prisvärda' }, { label: 'Leverans: 2026-11-27' }]}
  fomo={{
    image: cap,
    deliveryLabel: 'Garanterad leverans',
    deliveryDate: '2026-11-27',
    countdownLabel: 'Beställ inom',
    deadline: '2026-11-20T23:59:59',
    countdownUnits: { days: 'drg', hours: 'tim', minutes: 'min', seconds: 'sek' },
    ctaLabel: 'Börja designa',
    ctaOnClick: () => navigate('/configurator'),
  }}
  reveal
/>`,
    },
  ],
};

export default function HeroBannerV4Page() {
  return (
    <ComponentPage meta={meta}>
      <DemoCard
        title="Gradient field, cut-out, USP bar and the FOMO card"
        hint="Figma Crownstudent-MCP 90:26891 · mobile 96:30960 · switch the top-bar brand to Crownstudent for the Figma look · the countdown ticks every second"
        flush
      >
        <HeroBannerV4 {...HERO_V4} fomo={FOMO_V4} reveal />
      </DemoCard>
      <DemoCard title="Without the card" hint="`fomo` omitted — the USP row runs the full width">
        <HeroBannerV4 {...HERO_V4} />
      </DemoCard>
    </ComponentPage>
  );
}
